/**
 * 自定义管理全局的消息通知弹框
 * visible: boolean  是否显示，默认 false 隐藏
 * message: string 消息内容
 * duration?: number  显示时长（可选，默认5000ms）
 * onClick?: () => void 点击回调函数（可选）
 * 
 * 使用方式：
 * showCustomNotify({
    message: '',
    duration: 3000,
    onClick: () => {
    }
})
 */
import { ref } from 'vue'

interface NotifyOptions {
    message: string
    duration?: number
    onClick?: () => void
}

interface NotifyState {
    visible: boolean
    message: string
    duration: number
    onClick?: () => void
}

// 全局状态
const notifyState = ref<NotifyState>({
    visible: false,
    message: '',
    duration: 5000,
})

export function useCustomNotify() {
    const showNotify = (options: NotifyOptions) => {
        // 参数验证
        if (!options.message || typeof options.message !== 'string') {
            console.warn('CustomNotify: message is required and must be a string')
            return
        }

        // 如果已经显示，先隐藏
        if (notifyState.value.visible) {
            hideNotify()
            // 等待隐藏动画完成后再显示新的
            setTimeout(() => {
                showNewNotify(options)
            }, 300)
        } else {
            showNewNotify(options)
        }
    }

    const showNewNotify = (options: NotifyOptions) => {
        notifyState.value = {
            visible: true,
            message: options.message,
            duration: options.duration || 5000,
            onClick: options.onClick,
        }
    }

    const hideNotify = () => {
        notifyState.value.visible = false
    }

    // 页面卸载时清理
    const cleanup = () => {
        hideNotify()
    }

    // 监听页面卸载事件
    if (typeof window !== 'undefined') {
        window.addEventListener('beforeunload', cleanup)
        window.addEventListener('pagehide', cleanup)
    }

    return {
        showNotify,
        hideNotify,
        notifyState,
    }
}

// 创建全局实例
const globalNotifyInstance = useCustomNotify()

// 导出全局方法和状态
export const showCustomNotify = globalNotifyInstance.showNotify
export const hideCustomNotify = globalNotifyInstance.hideNotify
export { notifyState }
